import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Grid,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Paper,
  Button,
  Card,
  Collapse,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Modal,
  Fade,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";

import Dbutton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import ThreeWayMatchTable from "./purchaseorder";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import { format } from "date-fns";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TWMOverviewDashboard from "./TWMOverviewDashboard";
import { useOfflineTWM } from "../../../offline/hooks/useOfflineTWM";
import { twmRepository } from "../../../offline/repositories/twm.repository";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import deletePopupImg from "../../../assets/images/png/Group 2@2x.png";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { consumePendingChatbotUpload } from "../../../components/Chatbot/chatTransferStore";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";

const TWM_REQUIRED_FILE_COUNT = 3;
const TWM_REQUIRED_DOCUMENTS = [
  {
    key: "invoice_data",
    label: "Invoice",
    fields: [
      "invoice_number",
      "invoice_no",
      "bill_number",
      "bill_no",
      "invoice_date",
      "date",
      "invoice_amount",
      "total_amount",
      "amount",
      "purchase_order_number",
      "po_number",
      "po_no",
      "vendor_name",
      "supplier_name",
      "line_items",
      "items",
    ],
  },
  {
    key: "grn_data",
    label: "GRN",
    fields: [
      "grn_number",
      "grn_no",
      "grn_date",
      "goods_receipt_no",
      "receipt_number",
      "challan_number",
      "challan_no",
      "order_number",
      "date",
      "received_date",
      "vendor",
      "vendor_name",
      "line_items",
      "items",
    ],
  },
  {
    key: "po_data",
    label: "PO",
    fields: [
      "purchase_order_no",
      "purchase_order_number",
      "po_number",
      "po_no",
      "order_number",
      "order_date",
      "po_date",
      "vendor",
      "vendor_name",
      "supplier_name",
      "line_items",
      "items",
    ],
  },
];

const isPdfFile = (file) => {
  const fileName = String(file?.name || "").toLowerCase();
  const fileType = String(file?.type || "").toLowerCase();
  return (
    fileName.endsWith(".pdf") && (!fileType || fileType === "application/pdf")
  );
};

const normalizeFileName = (fileName) =>
  String(fileName || "")
    .trim()
    .toLowerCase();

const getDuplicateFileNames = (files = []) => {
  const seen = new Set();
  const duplicates = new Set();

  files.forEach((file) => {
    const normalizedName = normalizeFileName(file?.name);
    if (!normalizedName) return;
    if (seen.has(normalizedName)) {
      duplicates.add(file.name);
      return;
    }
    seen.add(normalizedName);
  });

  return Array.from(duplicates);
};

const hasMeaningfulExtractedValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulExtractedValue);
  if (typeof value === "object") {
    return Object.values(value).some(hasMeaningfulExtractedValue);
  }
  return String(value).trim() !== "";
};

const hasExtractedDocumentData = (documents, expectedFields = []) =>
  Array.isArray(documents) &&
  documents.some(
    (document) =>
      hasMeaningfulExtractedValue(document) &&
      expectedFields.some((field) =>
        hasMeaningfulExtractedValue(document?.[field]),
      ),
  );

const getTwmExtractionValidation = (data) => {
  const defaultMissingDocuments = TWM_REQUIRED_DOCUMENTS.map(
    ({ label }) => label,
  );
  if (!data || typeof data !== "object") {
    return {
      isValid: false,
      hasExpectedFileCount: false,
      failedFiles: [],
      missingDocuments: defaultMissingDocuments,
    };
  }

  const extractedFiles = Array.isArray(data.files) ? data.files : [];
  const failedFiles = extractedFiles.filter(
    (file) =>
      String(file?.status || "")
        .trim()
        .toLowerCase() !== "ok",
  );
  const missingDocuments = TWM_REQUIRED_DOCUMENTS.reduce(
    (missing, { key, label, fields }) =>
      hasExtractedDocumentData(data[key], fields)
        ? missing
        : [...missing, label],
    [],
  );

  return {
    isValid:
      extractedFiles.length === TWM_REQUIRED_FILE_COUNT &&
      failedFiles.length === 0 &&
      missingDocuments.length === 0,
    hasExpectedFileCount: extractedFiles.length === TWM_REQUIRED_FILE_COUNT,
    failedFiles,
    missingDocuments,
  };
};

const getTwmExtractionValidationMessage = ({
  hasExpectedFileCount,
  failedFiles = [],
  missingDocuments = [],
} = {}) => {
  if (!hasExpectedFileCount) {
    return "Upload must contain exactly three valid PDF documents: Invoice, GRN, and PO.";
  }
  if (failedFiles.length > 0) {
    return "Upload completed, but extraction failed for one or more files. Please upload valid Invoice, GRN, and PO PDFs.";
  }
  if (missingDocuments.length > 0) {
    return `Upload must include valid Invoice, GRN, and PO PDFs. Missing extracted data: ${missingDocuments.join(
      ", ",
    )}.`;
  }
  return "Upload must include valid Invoice, GRN, and PO PDFs.";
};

const hasSuccessfulTwmExtraction = (data) => {
  return getTwmExtractionValidation(data).isValid;
};

const LARGE_SNACK_MESSAGES = new Set([
  "Upload must contain exactly three valid PDF documents: Invoice, GRN, and PO.",
]);

// Status Chip Component for main table
const StatusChip = ({ status, type }) => {
  const config = {
    matched: { color: "#2E7D32", bgcolor: "#E8F5E9", icon: "âœ“" },
    partial: { color: "#ED6C02", bgcolor: "#FFF3E0", icon: "âš " },
    rejected: { color: "#D32F2F", bgcolor: "#FFEBEE", icon: "âœ—" },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor } = config[type] || config.default;
  const statusIcon =
    type === "matched" ? (
      <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
    ) : type === "partial" ? (
      <AccessTimeIcon sx={{ fontSize: 16, color: "#ED6C02" }} />
    ) : type === "rejected" ? (
      <CancelIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
    ) : (
      <AccessTimeIcon sx={{ fontSize: 16, color: "#757575" }} />
    );

  return (
    <Chip
      icon={statusIcon}
      label={status}
      size="small"
      sx={{
        fontFamily: "gilroyMedium",
        fontSize: "14px",
        fontWeight: 600,
        height: "30px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${color}20`,
        "& .MuiChip-icon": {
          marginLeft: "8px",
          marginRight: "-4px",
        },
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
};

// Helper to analyze quantity match status
const analyzeQuantityMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || !grnItems.length || !invoiceItems.length) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    // Check each PO item
    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poQty = parseFloat(poItem.qty || poItem.parsed_qty || 0);

      // Find matching GRN item
      const grnItem = grnItems.find(
        (item) => item.part_no === poPartNo || item.item_code === poPartNo,
      );

      // Find matching Invoice item
      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo),
      );

      const grnQty = parseFloat(
        grnItem?.quantity_received ||
          grnItem?.received_quantity ||
          grnItem?.received_q ||
          grnItem?.qty ||
          0,
      );
      const invQty = parseFloat(
        invoiceItem?.qty || invoiceItem?.parsed_qty || 0,
      );

      // If item not found in GRN or Invoice â†’ PARTIAL (not rejected)
      if (!grnItem || !invoiceItem) {
        partial++;
      }
      // âœ… MATCHED: Exact quantity match across all three
      else if (poQty === grnQty && poQty === invQty) {
        matched++;
      }
      // ðŸ”´ REJECTED: Quantity EXCEEDING in GRN or Invoice (greater than)
      else if (grnQty > poQty || invQty > poQty) {
        rejected++;
      }
      // ðŸŸ¡ PARTIAL: Quantity LESS in GRN or Invoice (smaller than)
      else if (grnQty < poQty || invQty < poQty) {
        partial++;
      } else {
        matched++;
      }

      totalChecked++;
    });

    // Determine overall status
    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze price match status
const analyzePriceMatch = (poData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length || !invoiceItems.length) {
      return { status: "No Data", type: "rejected" };
    }

    let matched = 0;
    let partial = 0;
    let rejected = 0;
    let totalChecked = 0;

    poItems.forEach((poItem) => {
      const poPartNo = poItem.part_no || poItem.item_code;
      const poPrice = parseFloat(poItem.rate || poItem.parsed_rate || 0);

      const invoiceItem = invoiceItems.find(
        (item) =>
          item.part_no === poPartNo ||
          item.cat_code?.toString().includes(poPartNo) ||
          item.description_of_goods?.includes(poPartNo),
      );

      // If item not found â†’ PARTIAL (not rejected)
      if (!invoiceItem) {
        partial++;
      } else {
        const invPrice = parseFloat(
          invoiceItem.rate || invoiceItem.parsed_rate || 0,
        );

        // ðŸ”´ REJECTED: Price INCREASED in invoice (greater than)
        if (invPrice > poPrice) {
          rejected++;
        }
        // ðŸŸ¡ PARTIAL: Price DECREASED in invoice (smaller than)
        else if (invPrice < poPrice) {
          partial++;
        }
        // âœ… MATCHED: Same price
        else {
          matched++;
        }
      }

      totalChecked++;
    });

    if (rejected > 0) return { status: "Rejected", type: "rejected" };
    if (partial > 0 && rejected === 0)
      return { status: "Partial", type: "partial" };
    if (matched === totalChecked && totalChecked > 0)
      return { status: "Matched", type: "matched" };

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

// Helper to analyze item match status
const analyzeItemMatch = (poData, grnData, invoiceData) => {
  try {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    if (!poItems.length) {
      return { status: "No PO Items", type: "rejected" };
    }

    const poItemCount = poItems.length;
    const grnItemCount = grnItems.length;
    const invoiceItemCount = invoiceItems.length;

    // ðŸ”´ REJECTED: GRN has MORE items than PO (EXCEEDING)
    if (grnItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // ðŸ”´ REJECTED: Invoice has MORE items than PO (EXCEEDING)
    if (invoiceItemCount > poItemCount) {
      return { status: "Rejected", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: GRN has LESS items than PO
    if (grnItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // ðŸŸ¡ PARTIAL: Invoice has LESS items than PO
    if (invoiceItemCount < poItemCount) {
      return { status: "Partial", type: "partial" };
    }

    // âœ… MATCHED: All item counts match
    if (poItemCount === grnItemCount && poItemCount === invoiceItemCount) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "rejected" };
  } catch (error) {
    return { status: "Error", type: "rejected" };
  }
};

const getMatchStatus = (result_json) => {
  try {
    const poItems = result_json?.po_data || [];
    const grnItems = result_json?.grn_data || [];
    const invoiceItems = result_json?.invoice_data || [];

    const normalize = (v) => String(v).trim().toLowerCase();

    let allMatched = true;

    poItems.forEach((poItem) => {
      const code = normalize(poItem?.part_no || poItem?.item_code);

      const grn = grnItems.find(
        (g) => normalize(g?.part_no || g?.item_code) === code,
      );
      const inv = invoiceItems.find(
        (i) => normalize(i?.part_no || i?.item_code) === code,
      );

      const poQty = parseFloat(poItem?.qty || 0);
      const grnQty = parseFloat(
        grn?.quantity_received ||
          grn?.received_quantity ||
          grn?.received_q ||
          grn?.qty ||
          0,
      );
      const invQty = parseFloat(inv?.qty || 0);

      if (
        isNaN(poQty) ||
        isNaN(grnQty) ||
        isNaN(invQty) ||
        poQty !== grnQty ||
        poQty !== invQty
      ) {
        allMatched = false;
      }
    });

    return allMatched ? "Matched" : "Partially Matched";
  } catch {
    return "Partially Matched";
  }
};

const DownloadMenuButton = ({ onCSV }) => {
  return (
    <IconButton onClick={onCSV} sx={{ p: 0.5 }}>
      <DownloadIcon />
    </IconButton>
  );
};

const EmptyState = ({ title = "No records found", subtitle }) => (
  <Box sx={{ textAlign: "center", py: 8, px: 2, color: "text.secondary" }}>
    <AssignmentTurnedInIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, fontFamily: "GilroyBold", fontSize: "18px" }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body2"
        sx={{ mt: 0.5, fontFamily: "gilroyMedium", fontSize: "14px" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Date Format
const formatDate = (date) => {
  if (!date) return "â€”";

  let day, month, year;

  // DD/MM/YYYY or DD-MM-YYYY
  if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(date)) {
    const parts = date.includes("/") ? date.split("/") : date.split("-");
    [day, month, year] = parts.map(Number);
  }
  // YYYY-M-D or YYYY-MM-DD
  else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(date)) {
    [year, month, day] = date.split("-").map(Number);
  }
  // Try JS Date fallback
  else {
    const parsed = new Date(date);
    if (isNaN(parsed)) return "â€”";
    day = parsed.getDate();
    month = parsed.getMonth() + 1;
    year = parsed.getFullYear();
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthName = monthNames[month - 1];
  const dayStr = day.toString().padStart(2, "0");

  return `${dayStr}-${monthName}-${year}`;
};

const normalizeTwmRecord = (record = {}) => {
  const twmMeta = record?.three_way_matching || {};
  return {
    ...record,
    token: record?.token || twmMeta?.token || record?.twm_token || "",
    match_code:
      record?.match_code || record?.twm_code || twmMeta?.twm_code || "",
    twm_code: record?.twm_code || record?.match_code || twmMeta?.twm_code || "",
    result_json:
      record?.result_json ||
      record?.extracted_data ||
      record?.three_way_matching_data ||
      {},
    approvers: record?.approvers || twmMeta?.approvers || [],
  };
};

// Updated ApprovalStatusTooltip component
const ApprovalStatusTooltip = ({ approvers }) => {
  if (!approvers || approvers.length === 0) return null;

  const approved = approvers.filter((a) => a.status === "APPROVED");
  const pending = approvers.filter((a) => a.status === "PENDING");
  const rejected = approvers.filter((a) => a.status === "REJECTED");

  return (
    <Box sx={{ p: 1.5, minWidth: 220 }}>
      {/* âœ… Approved */}
      {approved.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#0bf063ff",
              fontSize: "13px",
            }}
          >
            âœ… Approved ({approved.length})
          </Typography>
          {approved.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#f0fdf4",
                mb: 0.5,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: "#166534", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#308350ff", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* â³ Pending */}
      {pending.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "#e98b1fff",
              fontSize: "13px",
            }}
          >
            â³ Pending ({pending.length})
          </Typography>
          {pending.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fefce8",
                mb: 0.5,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 16, color: "#854d0e", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#713f12", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* âŒ Rejected */}
      {rejected.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "#991b1b", fontSize: "13px" }}
          >
            âŒ Rejected ({rejected.length})
          </Typography>
          {rejected.map((a) => (
            <Box
              key={a.id}
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                py: 0.5,
                borderRadius: 0.75,
                bgcolor: "#fef2f2",
                mb: 0.5,
              }}
            >
              <CancelIcon sx={{ fontSize: 16, color: "#991b1b", mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#7f1d1d", fontWeight: 500 }}
              >
                {a.name || a.id}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ThreeWayMatching = ({ uploadmode, onModeChange }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(loginData?.auth_token);
  const [user_id] = useState(loginData?.user_id);

  const [user_token] = useState(loginData?.user?.user_token);
  const [user_designation] = useState(loginData?.user?.designation);
  const user_role = loginData?.user?.role;

  // UI states
  const [mode, setMode] = useState(uploadmode);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [response, setResponse] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [overallProgress, setOverallProgress] = useState(0);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });
  const [isDragging, setIsDragging] = useState(false);

  const [getmatchdata, setMatchedData] = useState(null);
  const [matchedresponse, setMatchedResponse] = useState(null);
  const [summaryResponse, setSummaryResponse] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [savedTwmCode, setSavedTwmCode] = useState("");
  const [approvedTokens, setApprovedTokens] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewItemsDialog, setViewItemsDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const [nextTwmCode, setNextTwmCode] = useState("");

  // State for pagination (backend-driven)
  const [pageNumber, setPageNumber] = useState(() => {
    try {
      const stored = localStorage.getItem("three_way_matching_page_number");
      const n = parseInt(stored || "", 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    } catch {
      return 1;
    }
  });
  const [sortOrder, setSortOrder] = useState("desc");

  const [openOfflinePopup, setOpenOfflinePopup] = useState(false);

  const handleSortByInvoiceRef = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
  };

  // Sort current page data (backend pagination supplies 1 page at a time)
  const getSortedData = () => {
    return [...filterRecords].sort((a, b) => {
      const aTs = new Date(a?.created_at || 0).getTime();
      const bTs = new Date(b?.created_at || 0).getTime();

      // Primary sort: newest first (or oldest first when toggled)
      if (aTs !== bTs) {
        return sortOrder === "desc" ? bTs - aTs : aTs - bTs;
      }

      // Fallback tie-breaker: match code
      const aCode = a?.match_code || "";
      const bCode = b?.match_code || "";

      if (sortOrder === "desc") {
        return bCode.localeCompare(aCode);
      } else {
        return aCode.localeCompare(bCode);
      }
    });
  };

  // Refs for upload
  const controllerRef = useRef(null);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingChatAutoUploadRef = useRef(false);
  const handleUploadRef = useRef(null);

  const fetchNextCode = async () => {
    try {
      const { data } = await axios.post(
        API_ENDPOINT.peek_next_twm_code,
        { user_token },
        { headers: { Authorization: `Bearer ${auth_token}` } },
      );
      setNextTwmCode(data?.next_match_code || "");
    } catch {
      setNextTwmCode("");
    }
  };

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, []);

  const scrollByAmount = (dx) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const listColumnWidths = {
    matchCode: 180,
    supplierName: 220,
    poId: 150,
    poDate: 145,
    invoiceNumber: 165,
    invoiceDate: 145,
    grnNumber: 150,
    grnDate: 145,
    poTotal: 155,
    invoiceTotal: 165,
    quantityStatus: 150,
    priceStatus: 140,
    itemStatus: 135,
    matchingDate: 145,
    approvalStatus: 150,
    approveReject: 150,
    action: 120,
  };

  const resetUploadState = (options = {}) => {
    const { abortUpload = true } = options;

    if (abortUpload && controllerRef.current) {
      try {
        controllerRef.current.abort();
      } catch {
        // ignore
      }
      controllerRef.current = null;
    }

    pendingChatAutoUploadRef.current = false;
    setIsDragging(false);
    setSelectedFiles([]);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);

    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {
        // ignore
      }
    }
  };

  const addSelectedFiles = useCallback(
    (incomingFiles) => {
      const files = Array.from(incomingFiles || []);
      if (!files.length) return;

      const invalidFiles = files.filter((file) => !isPdfFile(file));
      const pdfFiles = files.filter(isPdfFile);

      if (invalidFiles.length > 0) {
        setSnack({
          open: true,
          type: "error",
          msg: "Only PDF documents are allowed. Excel, CSV, image, and other file types cannot be uploaded.",
        });
      }

      if (!pdfFiles.length) return;

      const duplicateFileNames = getDuplicateFileNames([
        ...selectedFiles,
        ...pdfFiles,
      ]);
      if (duplicateFileNames.length > 0) {
        setSnack({
          open: true,
          type: "error",
          msg: "Same files are not allowed.",
        });
        return;
      }

      const availableSlots = TWM_REQUIRED_FILE_COUNT - selectedFiles.length;
      if (availableSlots <= 0) {
        setSnack({
          open: true,
          type: "warning",
          msg: "Only three PDF documents can be selected at a time.",
        });
        return;
      }

      const acceptedFiles = pdfFiles.slice(0, availableSlots);
      if (acceptedFiles.length < pdfFiles.length) {
        setSnack({
          open: true,
          type: "warning",
          msg: "Only three PDF documents can be selected at a time.",
        });
      }

      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
      setResponse(null);
      setUploadStatus("idle");
      setOverallProgress(0);
    },
    [selectedFiles],
  );

  useEffect(() => {
    if (uploadmode && ["main", "upload", "view"].includes(uploadmode)) {
      setMode(uploadmode);
    }
  }, [uploadmode]);

  const go = (next) => {
    if (mode === "upload" && next !== "upload") {
      resetUploadState({ abortUpload: true });
    }
    if (typeof onModeChange === "function") onModeChange(next);
    else setMode(next);
  };

  useEffect(() => {
    if (mode !== "upload" || !dropRef.current) return;
    const el = dropRef.current;
    const onDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);
    const onDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) {
        addSelectedFiles(files);
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [mode, addSelectedFiles]);

  // File upload handlers
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    addSelectedFiles(files);
  };
  const handleRemoveFile = (fileIndex) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);
    if (fileInputRef.current) {
      try {
        fileInputRef.current.value = "";
      } catch {
        // ignore
      }
    }
  };

  const handleCancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      setUploadStatus("idle");
      setOverallProgress(0);
      setSnack({ open: true, type: "info", msg: "Upload cancelled." });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || uploadStatus === "uploading") return;

    if (getDuplicateFileNames(selectedFiles).length > 0) {
      setSnack({
        open: true,
        type: "error",
        msg: "Same file name is not allowed. Please rename the PDF files and upload again.",
      });
      return;
    }

    if (selectedFiles.length !== TWM_REQUIRED_FILE_COUNT) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please select exactly three PDF documents: Invoice, GRN, and PO.",
      });
      return;
    }

    if (!selectedFiles.every(isPdfFile)) {
      setSnack({
        open: true,
        type: "error",
        msg: "Only PDF documents are allowed.",
      });
      return;
    }

    setUploadStatus("uploading");
    setOverallProgress(0);
    setResponse(null);

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("files", file));

    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const res = await axios.post(API_ENDPOINT.extractdoc, formData, {
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
        signal: controller.signal,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setOverallProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      const extractedData = res?.data || {};
      const extractionValidation = getTwmExtractionValidation(extractedData);
      const hasValidExtraction = extractionValidation.isValid;
      setResponse(extractedData);
      setSnack({
        open: true,
        type: hasValidExtraction ? "success" : "warning",
        msg: hasValidExtraction
          ? "Files uploaded & extracted successfully."
          : getTwmExtractionValidationMessage(extractionValidation),
      });
      setUploadStatus(hasValidExtraction ? "success" : "error");
      setOverallProgress(100);
    } catch (err) {
      if (axios.isCancel?.(err)) return;
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Upload failed. Please try again.",
      });
      setUploadStatus("error");
    } finally {
      controllerRef.current = null;
    }
  };
  handleUploadRef.current = handleUpload;
  console.log(matchedresponse);
  // Search handlers
  const filterRecords = (matchedresponse || []).filter((item) => {
    const q = String(searchTerm || "").toLowerCase();
    const safeLower = (v) => String(v ?? "").toLowerCase();
    const result = item?.result_json || {};
    return (
      safeLower(item?.match_code).includes(q) ||
      (result?.grn_data || []).some((grn) =>
        safeLower(
          grn?.vendor_name || grn?.vendor || grn?.receiver_name,
        ).includes(q),
      ) ||
      (result?.po_data || []).some((poNO) =>
        safeLower(poNO?.purchase_order_no).includes(q),
      ) ||
      (result?.invoice_data || []).some((invoiceNO) =>
        safeLower(invoiceNO?.invoice_number).includes(q),
      )
    );
  });

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  useEffect(() => {
    setPageNumber(1);
  }, [searchTerm]);

  // Upload status chip
  const statusChip = useMemo(() => {
    switch (uploadStatus) {
      case "uploading":
        return (
          <Chip
            label={`Uploading ${overallProgress}%`}
            color="info"
            size="small"
            sx={{ fontSize: "12px" }}
          />
        );
      case "success":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Success"
            color="success"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      case "error":
        return (
          <Chip
            label="Error"
            color="error"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
      default:
        return (
          <Chip
            label="Waiting to upload"
            variant="outlined"
            sx={{ fontSize: "12px" }}
            size="small"
          />
        );
    }
  }, [uploadStatus, overallProgress]);

  const canSaveMatching = useMemo(
    () => hasSuccessfulTwmExtraction(response),
    [response],
  );

  // File size display
  const formatKB = (bytes) => `${(bytes / 1024).toFixed(2)} KB`;

  const handleSaveMatching = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please upload Invoice, GRN, and PO PDF documents before saving.",
      });
      return;
    }

    if (selectedFiles.length !== TWM_REQUIRED_FILE_COUNT) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please upload exactly three PDF documents: Invoice, GRN, and PO.",
      });
      return;
    }

    if (!selectedFiles.every(isPdfFile)) {
      setSnack({
        open: true,
        type: "error",
        msg: "Only PDF documents are allowed.",
      });
      return;
    }

    const extractionValidation = getTwmExtractionValidation(response);
    if (!extractionValidation.isValid) {
      setSnack({
        open: true,
        type: "warning",
        msg: `Cannot save. ${getTwmExtractionValidationMessage(extractionValidation)}`,
      });
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("file", file));
    formData.append("user_token", user_token);
    formData.append("match_code", nextTwmCode || "");
    formData.append(
      "result_json",
      typeof response === "string" ? response : JSON.stringify(response || {}),
    );

    try {
      const request = await axios({
        url: `${API_ENDPOINT.save_three_way_matching}`,
        data: formData,
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSavedTwmCode(nextTwmCode);
      setShowSuccessDialog(true);
      go("main");
      // await fetchMatchedData();
      await refetch();
      await fetchDashboardSummary();
      setSnack({
        open: true,
        type: "success",
        msg: "Matching saved successfully!",
      });

      setSelectedFiles([]);
      setResponse(null);
      setUploadStatus("idle");
      setOverallProgress(0);
    } catch (err) {
      setSnack({
        open: true,
        type: "error",
        msg: err?.response?.data?.error || "Save failed. Please try again.",
      });
    }
  };
  const handleSaveMatchingWithLoader = async () => {
    setSaving(true);
    try {
      await handleSaveMatching(); // your original save logic
    } finally {
      setSaving(false);
    }
  };

  // const fetchMatchedData = async () => {
  //   try {
  //     const res = await axios.post(
  //       `${API_ENDPOINT.get_three_way_matching}`,
  //       { user_token },
  //       { headers: { Authorization: `Bearer ${auth_token}` } }
  //     );

  //     const twmList = res?.data || [];
  //     setMatchedResponse(twmList);

  //     // Set latest one
  //     const latest = twmList?.[0]?.result_json || null;
  //     setMatchedData(latest);

  //     const enrichedWithApprovers = await Promise.all(
  //       twmList.map(async (twm) => {
  //         try {
  //           const approverRes = await axios.post(
  //             `${API_ENDPOINT.get_twm_approver_names}`,
  //             { twm_token: twm.token },
  //             { headers: { Authorization: `Bearer ${auth_token}` } }
  //           );

  //           return {
  //             ...twm,
  //             approvers: approverRes.data?.approvers || [],
  //           };
  //         } catch (err) {
  //           console.error("Failed to fetch approvers for TWM:", twm.token);
  //           return {
  //             ...twm,
  //             approvers: [],
  //           };
  //         }
  //       })
  //     );

  //     // Optional: set this enriched list to another state
  //     setMatchedResponse(enrichedWithApprovers);
  //   } catch (error) {
  //     console.error("Error fetching TWM data", error);
  //   }
  // };

  // combine api

  // const fetchMatchedData = async () => {
  //   try {
  //     const res = await axios.post(
  //       `${API_ENDPOINT.get_three_way_matching_with_approvers}`,
  //       { user_token },
  //       { headers: { Authorization: `Bearer ${auth_token}` } }
  //     );

  //     const twmList = res?.data || [];
  //     setMatchedResponse(twmList);

  //     // Set latest one
  //     const latest = twmList?.[0]?.result_json || null;
  //     setMatchedData(latest);

  //   } catch (error) {
  //     console.error("Error fetching TWM data", error);
  //   }
  // };
  // useEffect(() => {
  //   fetchMatchedData();
  // }, []);

  //twm offline and online

  const { twmList, loading, paginationData, refetch } = useOfflineTWM(
    auth_token,
    user_token,
    pageNumber,
  );

  const fetchDashboardSummary = useCallback(async () => {
    if (!auth_token || !user_token) return;

    setSummaryLoading(true);
    try {
      const { data } = await axios.post(
        API_ENDPOINT.get_three_way_matching_summary,
        {
          user_token,
          status: null,
          role: null,
          match_code: null,
          from_date: null,
          to_date: null,
          page: 1,
          page_size: 10,
        },
        { headers: { Authorization: `Bearer ${auth_token}` } },
      );

      setSummaryResponse(data || {});
    } catch (error) {
      console.error("Error fetching TWM dashboard summary", error);
      setSummaryResponse(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [auth_token, user_token]);

  useEffect(() => {
    fetchDashboardSummary();
  }, [fetchDashboardSummary]);

  useEffect(() => {
    const handleOnline = async () => {
      console.log("Network back online -> syncing TWM...");
      await refetch();
      await fetchDashboardSummary();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [fetchDashboardSummary, refetch]);

  useEffect(() => {
    if (!twmList.length) return;

    const sortedByLatest = [...twmList].sort(
      (a, b) =>
        new Date(b?.created_at || 0).getTime() -
        new Date(a?.created_at || 0).getTime(),
    );

    setMatchedResponse(sortedByLatest);
    setMatchedData(sortedByLatest[0]?.result_json || null);
  }, [twmList]);

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action, three_way_search } = JSON.parse(chatActionRaw);
    console.log(three_way_search);

    if (action === "export_data") {
      if (!matchedresponse || matchedresponse.length === 0) {
        // wait until data is loaded
        return;
      }

      sessionStorage.removeItem("CHAT_ACTION");
      downloadAllCSV();
    }

    if (action === "search_records") {
      setSearchTerm(three_way_search);
    }

    if (action === "create_twm") {
      fetchNextCode();
      go("upload");
    }

    if (action === "open_twm_specific_record") {
      console.log("hii");
      if (!matchedresponse || matchedresponse.length === 0) return;

      const safeCode = three_way_search?.toString().trim().toLowerCase();

      console.log(safeCode, "safe");

      const matchedCard = matchedresponse.find(
        (item) => item.match_code?.toLowerCase() === safeCode,
      );

      console.log(matchedCard);
      if (!matchedCard) {
        console.warn("P2P record not found:", safeCode);
        return;
      }

      handleViewItems(matchedCard);
      sessionStorage.removeItem("CHAT_ACTION");
    }
  }, [matchedresponse]);

  useEffect(() => {
    if (mode !== "upload" || uploadStatus === "uploading") return;

    const pendingUpload = consumePendingChatbotUpload("create_twm");
    if (!pendingUpload?.files?.length) return;

    const pendingFiles = Array.from(pendingUpload.files || []);
    const pendingPdfFiles = pendingFiles.filter(isPdfFile);
    if (
      pendingFiles.length !== TWM_REQUIRED_FILE_COUNT ||
      pendingPdfFiles.length !== TWM_REQUIRED_FILE_COUNT
    ) {
      setSnack({
        open: true,
        type: "warning",
        msg: "Please select exactly three PDF documents: Invoice, GRN, and PO.",
      });
      return;
    }

    if (getDuplicateFileNames(pendingPdfFiles).length > 0) {
      setSnack({
        open: true,
        type: "error",
        msg: "Same file name is not allowed. Please rename the PDF files and upload again.",
      });
      return;
    }

    setSelectedFiles(pendingPdfFiles);
    setResponse(null);
    setUploadStatus("idle");
    setOverallProgress(0);
    pendingChatAutoUploadRef.current = true;
  }, [mode, uploadStatus]);

  useEffect(() => {
    if (!pendingChatAutoUploadRef.current) return;
    if (mode !== "upload") return;
    if (!selectedFiles.length || uploadStatus === "uploading") return;

    pendingChatAutoUploadRef.current = false;
    handleUploadRef.current?.();
  }, [mode, selectedFiles, uploadStatus]);

  const buildViewDataFromMatches = (matchesObj) => {
    if (!matchesObj || typeof matchesObj !== "object") return null;
    return {
      poData: Array.isArray(matchesObj.po_data) ? matchesObj.po_data : [],
      invoiceData: Array.isArray(matchesObj.invoice_data)
        ? matchesObj.invoice_data
        : [],
      grnData: Array.isArray(matchesObj.grn_data) ? matchesObj.grn_data : [],
    };
  };

  const toggleReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: !m[id] }));
  };

  const closeReject = (id) => {
    // setOpenMap((m) => ({ ...m, [id]: false }));
  };

  const approveAndRejectDoc = async (action, twm_token) => {
    try {
      if (!navigator.onLine) {
        await twmRepository.saveOfflineAction(action, twm_token, user_token);

        setOpenOfflinePopup(true);
      } else {
        const formData = new FormData();
        formData.append("action", action);
        formData.append("user_token", user_token);
        formData.append("twm_token", twm_token);

        await axios.post(API_ENDPOINT.approve_reject_twm, formData, {
          headers: { Authorization: `Bearer ${auth_token}` },
        });
      }

      if (action === "accept") {
        setApprovedTokens((prev) => [...prev, twm_token]);
      }

      await refetch();
      await fetchDashboardSummary();
    } catch (err) {
      console.error(err);
    }
  };

  const buildRowsFromResponse = (dataObj = {}) => {
    const rows = [];
    const pick = (obj, keys) => {
      if (!obj) return "";
      for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      return "";
    };

    // ---- PO ----
    const poList = Array.isArray(dataObj.po_data) ? dataObj.po_data : [];
    poList.forEach((po) => {
      const items = Array.isArray(po.line_items) ? po.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "PO",
          doc_no: po.purchase_order_no || po.order_number || "",
          doc_date: po.order_date || "",
          vendor: po.vendor || "",
          company_business_unit: pick(po, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(po, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li["qty."] != null
              ? li["qty."]
              : li.qty != null
                ? li.qty
                : li.quantity_received != null
                  ? li.quantity_received
                  : li.received_quantity != null
                    ? li.received_quantity
                    : li.received_q != null
                      ? li.received_q
                      : "",
          uom: li.uom || "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
                ? li.unit_price
                : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
                ? li.line_total
                : li.parsed_amount != null
                  ? li.parsed_amount
                  : "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(po, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    // ---- GRN ----
    const grnList = Array.isArray(dataObj.grn_data) ? dataObj.grn_data : [];
    grnList.forEach((grn) => {
      const items = Array.isArray(grn.line_items) ? grn.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "GRN",
          doc_no: grn.order_number || "",
          doc_date: grn.date || "",
          vendor: grn.vendor_name || "",
          company_business_unit: pick(grn, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(grn, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.quantity_received != null
              ? li.quantity_received
              : li.received_quantity != null
                ? li.received_quantity
                : li.received_q != null
                  ? li.received_q
                  : li["qty."] != null
                    ? li["qty."]
                    : li.qty != null
                      ? li.qty
                      : "",
          uom: li.uom || "",
          unit_price:
            li.rate != null
              ? li.rate
              : li.unit_price != null
                ? li.unit_price
                : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
                ? li.line_total
                : li.parsed_amount != null
                  ? li.parsed_amount
                  : "",
          lot_number: li.lot_number || "",
          expiry_date: li.expiry_date || "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(grn, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    // ---- INVOICE ----
    const invList = Array.isArray(dataObj.invoice_data)
      ? dataObj.invoice_data
      : [];
    invList.forEach((inv) => {
      const items = Array.isArray(inv.line_items) ? inv.line_items : [];
      items.forEach((li) => {
        rows.push({
          source: "INVOICE",
          doc_no: inv.invoice_number || "",
          doc_date: inv.invoice_date || "",
          vendor: "",
          company_business_unit: pick(inv, [
            "company",
            "business_unit",
            "businessUnit",
            "org_unit",
            "org",
          ]),
          location_plant: pick(inv, ["location", "plant", "delivery_location"]),
          hsn: li.hsn_code || li.hsn || "",
          part_no: li.part_no || li.item_code || li.code || "",
          description:
            li.description_of_goods || li.item_name || li.description || "",
          qty:
            li.qty != null
              ? li.qty
              : li["qty."] != null
                ? li["qty."]
                : li.quantity_received != null
                  ? li.quantity_received
                  : li.received_quantity != null
                    ? li.received_quantity
                    : li.received_q != null
                      ? li.received_q
                      : "",
          uom: li.uom || "",
          unit_price:
            li.unit_price != null
              ? li.unit_price
              : li.rate != null
                ? li.rate
                : "",
          tax_percent: pick(li, [
            "tax_percent",
            "gst_percent",
            "tax_percentage",
          ]),
          tax_type: pick(li, ["tax_type", "gst_type", "tax_category"]),
          tax_amount: pick(li, ["tax_amount", "gst_amount"]),
          amount:
            li.amount != null
              ? li.amount
              : li.line_total != null
                ? li.line_total
                : li.parsed_amount != null
                  ? li.parsed_amount
                  : "",
          po_ref: inv.purchase_order_number || "",
          variance_tax_other: pick(li, [
            "variance_tax_other",
            "tax_variance",
            "other_variance",
          ]),
          remarks: pick(li, ["remarks", "remark", "exception_notes", "notes"]),
          attachments: pick(inv, [
            "attachments",
            "attachment",
            "attachment_url",
            "document_url",
          ]),
        });
      });
    });

    return rows;
  };

  const toCSV = (dataObj = {}) => {
    const rows = buildRowsFromResponse(dataObj);
    if (rows.length === 0) return "source\n"; // empty CSV with header

    // Preferred header order, plus any extras we discover
    const preferred = [
      "source",
      "doc_no",
      "doc_date",
      "po_ref",
      "vendor",
      "part_no",
      "description",
      "hsn",
      "qty",
      "uom",
      "unit_price",
      "amount",
      "lot_number",
      "expiry_date",
    ];

    // Build union of keys actually present
    const found = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => found.add(k)));

    // Final headers = preferred (if present) + any remaining keys
    const headers = [
      ...preferred.filter((k) => found.has(k)),
      ...[...found].filter((k) => !preferred.includes(k)),
    ];

    // CSV escape
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      // NOTE: we intentionally do not strip commas; we escape them correctly.
    };

    const lines = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => esc(row[h])).join(",")),
    ];

    return lines.join("\n");
  };

  const downloadCSV = (card) => {
    try {
      const csv = toCSV(card?.result_json || {});
      const filename = `${card.match_code || card.twm_code || "twm"}-data.csv`;
      // Add BOM for Excel compatibility
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setSnack({ open: true, type: "error", msg: "Failed to download CSV." });
    }
  };

  const downloadAllCSV = () => {
    try {
      if (!matchedresponse || matchedresponse.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }

      // Build rows from ALL records
      let allRows = [];

      matchedresponse.forEach((card) => {
        const rows = buildRowsFromResponse(card?.result_json || {});
        const enrichedRows = rows.map((row) => ({
          match_code: card.match_code || card.twm_code || "",
          ...row,
        }));
        allRows = allRows.concat(enrichedRows);
      });

      if (allRows.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No line items found." });
        return;
      }

      // Preferred header order (we'll append any additional keys found)
      const preferredHeaders = [
        "match_code",
        "source",
        "doc_no",
        "doc_date",
        "po_ref",
        "vendor",
        "part_no",
        "description",
        "hsn",
        "qty",
        "uom",
        "unit_price",
        "amount",
        "lot_number",
        "expiry_date",
      ];

      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const found = new Set();
      allRows.forEach((r) => Object.keys(r || {}).forEach((k) => found.add(k)));
      const headers = [
        ...preferredHeaders.filter((h) => found.has(h)),
        ...[...found].filter((k) => !preferredHeaders.includes(k)),
      ];

      const csv = [
        headers.join(","),
        ...allRows.map((row) => headers.map((h) => esc(row[h])).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `three_way_matching_all_records.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, type: "error", msg: "Failed to export data." });
    }
  };

  function downloadDashboardReportCSV() {
    try {
      if (!matchedresponse || matchedresponse.length === 0) {
        setSnack({ open: true, type: "warning", msg: "No data to export." });
        return;
      }

      const rows = matchedresponse.map((card) => {
        const rj = card?.result_json || card?.extracted_data || {};
        const poData = rj?.po_data || [];
        const grnData = rj?.grn_data || [];
        const invoiceData = rj?.invoice_data || [];

        const po = poData?.[0] || {};
        const grn = grnData?.[0] || {};
        const invoice = invoiceData?.[0] || {};

        const quantityMatchStatus = analyzeQuantityMatch(
          poData,
          grnData,
          invoiceData,
        );
        const priceMatchStatus = analyzePriceMatch(poData, invoiceData);
        const itemMatchStatus = analyzeItemMatch(poData, grnData, invoiceData);

        const statuses = [
          quantityMatchStatus.status,
          priceMatchStatus.status,
          itemMatchStatus.status,
        ];
        const hasRejected = statuses.includes("Rejected");
        const hasPartial = statuses.includes("Partial");
        const matching_status = hasRejected
          ? "Exception"
          : hasPartial
            ? "Partially Matched"
            : "Matched";

        return {
          match_code: card.match_code || card.twm_code || "",
          matching_status,
          exception_flag: hasRejected ? "Yes" : "No",
          vendor_name: po?.vendor || grn?.vendor_name || grn?.vendor || "",
          company_business_unit:
            po?.business_unit ||
            po?.company ||
            grn?.business_unit ||
            grn?.company ||
            invoice?.business_unit ||
            invoice?.company ||
            "",
          location_plant:
            po?.location ||
            po?.plant ||
            grn?.location ||
            grn?.plant ||
            invoice?.location ||
            invoice?.plant ||
            "",
          po_number: po?.purchase_order_no || "",
          po_bill_date: po?.order_date || "",
          invoice_number: invoice?.invoice_number || "",
          invoice_date: invoice?.invoice_date || "",
          grn_number: grn?.grn_number || grn?.grn_no || "",
          grn_bill_date: grn?.grn_bill_date || grn?.bill_date || "",
          po_grand_total:
            po?.grand_total || po?.total_amount || po?.amount || "",
          invoice_grand_total:
            invoice?.invoice_amount ||
            invoice?.total_amount ||
            invoice?.amount ||
            "",
          quantity_status: quantityMatchStatus.status,
          price_status: priceMatchStatus.status,
          item_status: itemMatchStatus.status,
          matching_date: card?.created_at || "",
          approval_status: card?.status || "",
        };
      });

      const preferredHeaders = [
        "match_code",
        "matching_status",
        "exception_flag",
        "vendor_name",
        "company_business_unit",
        "location_plant",
        "po_number",
        "po_bill_date",
        "invoice_number",
        "invoice_date",
        "grn_number",
        "grn_bill_date",
        "po_grand_total",
        "invoice_grand_total",
        "quantity_status",
        "price_status",
        "item_status",
        "matching_date",
        "approval_status",
      ];

      const found = new Set();
      rows.forEach((r) => Object.keys(r || {}).forEach((k) => found.add(k)));
      const headers = [
        ...preferredHeaders.filter((h) => found.has(h)),
        ...[...found].filter((k) => !preferredHeaders.includes(k)),
      ];

      const esc = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const csv = [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => esc(row[h])).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "three_way_matching_dashboard_report.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        type: "error",
        msg: "Failed to export dashboard report.",
      });
    }
  }

  const handleViewItems = (card) => {
    setSelectedCard(card.result_json || card);
    setViewItemsDialog(true);
  };

  // Helper function to check if current user is an approver and can take action
  const canUserApproveReject = (card) => {
    if (!card.approvers || !user_id) return false;

    // Convert approvers object to array if needed
    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
        ? Object.entries(card.approvers).map(([id, status]) => ({
            id,
            status,
            name: id,
          }))
        : [];

    // Find current user in approvers
    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id,
    );

    // User is an approver and hasn't taken action yet (status is PENDING or doesn't exist)
    return currentUserApprover && currentUserApprover.status === "PENDING";
  };

  // Helper function to get current user's approval status
  const getUserApprovalStatus = (card) => {
    if (!card.approvers || !user_id) return null;

    const approversArray = Array.isArray(card.approvers)
      ? card.approvers
      : card.approvers
        ? Object.entries(card.approvers).map(([id, status]) => ({
            id,
            status,
            name: id,
          }))
        : [];

    const currentUserApprover = approversArray.find(
      (approver) =>
        approver.id === user_id.toString() || approver.id === user_id,
    );

    return currentUserApprover ? currentUserApprover.status : null;
  };

  // Helper function to check if current user is an approver for any card
  const isUserAnApprover = () => {
    if (!matchedresponse || !user_id) return false;

    return matchedresponse.some((card) => {
      if (!card.approvers) return false;

      const approversArray = Array.isArray(card.approvers)
        ? card.approvers
        : card.approvers
          ? Object.entries(card.approvers).map(([id, status]) => ({
              id,
              status,
              name: id,
            }))
          : [];

      return approversArray.some(
        (approver) =>
          approver.id === user_id.toString() || approver.id === user_id,
      );
    });
  };

  const userIsApprover = isUserAnApprover();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <>
      <Box>
        {/* MAIN PAGE: search, cards, button (no upload logic/search on upload) */}

        {/* <Box sx={{ width: "100%", mb: 1.5, ml:2.5}}>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      color="primary"
                      gutterBottom
                      sx={{
                        fontFamily: "GilroyBold",
                        fontSize: isMobile ? "16px" : "20px",
                        lineHeight: 1.3,
                      }}
                    >
                      Three Way Matching Summary
                    </Typography>
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "14px",
                        fontFamily:"gilroyMedium",
                        lineHeight: 1.5,
                      }}
                    >
                      Review all matched purchase orders, invoices, and GRNs.
                    </Typography>
                  </Box> */}
        {mode === "main" && (
          <>
            {/* Top bar with search + button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "nowrap",
                gap: 2,
                p: 2,
                "& input": {
                  fontFamily: "gilroyMedium",
                  fontSize: "14px",
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: "420px" }}>
                <SearchBar value={searchTerm} onChange={handleSearchChange} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadAllCSV}
                  sx={{
                    fontFamily: "gilroyMedium",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    fontSize: "14px",
                    minHeight: "42px",
                  }}
                >
                  Export Report
                </Button>

                {![
                  "Finance Analyst",
                  "Invoice Analyst",
                  "Business Finance",
                  "Account Payable",
                  "Buyer",
                ].includes(user_designation) && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={async () => {
                      await fetchNextCode();
                      go("upload");
                    }}
                    sx={{
                      fontFamily: "GilroyBold",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                      fontSize: "15px",
                      minHeight: "46px",
                      px: 2.25,
                    }}
                  >
                    Create Matching
                  </Button>
                )}
              </Box>
            </Box>

            {/* Cards Section */}
            {matchedresponse === null ? (
              // still loading the list
              <Box sx={{ px: 2, py: 4 }}>
                <LinearProgress />
              </Box>
            ) : filterRecords.length === 0 ? (
              // no records OR no records match the current search
              <EmptyState
                title="No records found"
                subtitle={
                  [
                    "Finance Analyst",
                    "Invoice Analyst",
                    "Business Finance",
                    "Account Payable",
                    "Buyer",
                  ].includes(user_designation)
                    ? "Try clearing the search."
                    : "Try clearing the search, or run a 3-Way Match to create your first record."
                }
              />
            ) : (
              <Grid container p={2}>
                <Grid item xs={12}>
                  <Box sx={{ width: "100%", mx: "auto" }}>
                    {/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadAllCSV}
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: 2,
                        fontSize: "14px",
                        mb: 5,
                      }}
                    >
                      Download All
                    </Button>
                  </Box> */}

                    {/* Unified Paper for Table + Pagination */}
                    <Paper
                      elevation={2}
                      sx={{
                        fontFamily: "gilroyMedium",
                        borderRadius: "10px",
                        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden", // ensures seamless pagination alignment
                      }}
                    >
                      {/* Table Container */}
                      <TableContainer
                        ref={scrollRef}
                        sx={{
                          maxHeight: "60vh",
                          overflowX: "auto",
                          overflowY: "auto",
                          WebkitOverflowScrolling: "touch",
                          "&::-webkit-scrollbar": { height: 0, width: 0 },
                        }}
                      >
                        <Table
                          size="small"
                          stickyHeader
                          sx={{
                            minWidth: userIsApprover ? 2100 : 1950, // Increased width for new columns
                            tableLayout: "fixed",
                            borderRight: "1px solid #e2e8f0",
                          }}
                        >
                          {/* ===== Table Head ===== */}
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: "#f8fafc",
                                "& th": {
                                  py: 1.2,
                                  borderBottom: "2px solid #e2e8f0",
                                  fontWeight: 700,
                                  fontFamily: "GilroyBold",
                                  fontSize: "14px",
                                  color: "#334155",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  whiteSpace: "nowrap",
                                  left: 0,
                                  zIndex: 4,
                                  width: listColumnWidths.matchCode,
                                  minWidth: listColumnWidths.matchCode,
                                  maxWidth: listColumnWidths.matchCode,
                                  backgroundColor: "#f8fafc",
                                  cursor: "pointer",
                                  boxShadow: "2px 0 0 #e2e8f0",
                                  "&:hover": { backgroundColor: "#f1f5f9" },
                                }}
                                onClick={() => handleSortByInvoiceRef()}
                              >
                                P2P ID {sortOrder === "desc" ? "↓" : "↑"}
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.supplierName,
                                  minWidth: listColumnWidths.supplierName,
                                  maxWidth: listColumnWidths.supplierName,
                                }}
                              >
                                Supplier Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poId,
                                  minWidth: listColumnWidths.poId,
                                  maxWidth: listColumnWidths.poId,
                                }}
                              >
                                PO ID
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poDate,
                                  minWidth: listColumnWidths.poDate,
                                  maxWidth: listColumnWidths.poDate,
                                }}
                              >
                                PO Bill Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceNumber,
                                  minWidth: listColumnWidths.invoiceNumber,
                                  maxWidth: listColumnWidths.invoiceNumber,
                                }}
                              >
                                Invoice Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceDate,
                                  minWidth: listColumnWidths.invoiceDate,
                                  maxWidth: listColumnWidths.invoiceDate,
                                }}
                              >
                                Invoice Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.grnNumber,
                                  minWidth: listColumnWidths.grnNumber,
                                  maxWidth: listColumnWidths.grnNumber,
                                }}
                              >
                                GRN Number
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.grnDate,
                                  minWidth: listColumnWidths.grnDate,
                                  maxWidth: listColumnWidths.grnDate,
                                }}
                              >
                                GRN Bill Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.poTotal,
                                  minWidth: listColumnWidths.poTotal,
                                  maxWidth: listColumnWidths.poTotal,
                                }}
                              >
                                PO Grand Total
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.invoiceTotal,
                                  minWidth: listColumnWidths.invoiceTotal,
                                  maxWidth: listColumnWidths.invoiceTotal,
                                }}
                              >
                                Invoice Grand Total
                              </TableCell>

                              {/* New Status Columns */}
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.quantityStatus,
                                  minWidth: listColumnWidths.quantityStatus,
                                  maxWidth: listColumnWidths.quantityStatus,
                                }}
                              >
                                Quantity Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.priceStatus,
                                  minWidth: listColumnWidths.priceStatus,
                                  maxWidth: listColumnWidths.priceStatus,
                                }}
                              >
                                Price Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.itemStatus,
                                  minWidth: listColumnWidths.itemStatus,
                                  maxWidth: listColumnWidths.itemStatus,
                                }}
                              >
                                Item Status
                              </TableCell>

                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.matchingDate,
                                  minWidth: listColumnWidths.matchingDate,
                                  maxWidth: listColumnWidths.matchingDate,
                                }}
                              >
                                Matching Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  whiteSpace: "nowrap",
                                  width: listColumnWidths.approvalStatus,
                                  minWidth: listColumnWidths.approvalStatus,
                                  maxWidth: listColumnWidths.approvalStatus,
                                }}
                              >
                                Approval Status
                              </TableCell>

                              {userIsApprover && (
                                <TableCell
                                  sx={{
                                    position: "sticky",
                                    right: listColumnWidths.action,
                                    width: listColumnWidths.approveReject,
                                    minWidth: listColumnWidths.approveReject,
                                    maxWidth: listColumnWidths.approveReject,
                                    zIndex: 3,
                                    backgroundColor: "#f8fafc",
                                    whiteSpace: "nowrap",
                                    borderLeft: "1px solid #e2e8f0",
                                    boxShadow:
                                      "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                                  }}
                                >
                                  Approve / Reject
                                </TableCell>
                              )}

                              <TableCell
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  width: listColumnWidths.action,
                                  minWidth: listColumnWidths.action,
                                  maxWidth: listColumnWidths.action,
                                  zIndex: 5,
                                  backgroundColor: "#f8fafc",
                                  textAlign: "center",
                                  borderLeft: "1px solid #e2e8f0",
                                  boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                                }}
                              >
                                Action
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          {/* ===== Table Body ===== */}
                          <TableBody>
                            {getSortedData().map((card, idx) => {
                              const po = card.result_json?.po_data?.[0];
                              const invoice =
                                card.result_json?.invoice_data?.[0];
                              const grn = card.result_json?.grn_data?.[0];

                              const poTotalAmt = po?.total_amount
                                ? Number(
                                    po.total_amount
                                      .toString()
                                      .replace(/,/g, ""),
                                  ).toLocaleString("en-IN")
                                : "â€”";
                              const invoiceTotalAmt = invoice?.invoice_amount
                                ? Number(
                                    invoice.invoice_amount
                                      .toString()
                                      .replace(/,/g, ""),
                                  ).toLocaleString("en-IN")
                                : "â€”";

                              const priceMatch =
                                poTotalAmt === invoiceTotalAmt
                                  ? "Matched"
                                  : "Exception Found";

                              const quantityMatchStatus = analyzeQuantityMatch(
                                card.result_json?.po_data,
                                card.result_json?.grn_data,
                                card.result_json?.invoice_data,
                              );

                              const priceMatchStatus = analyzePriceMatch(
                                card.result_json?.po_data,
                                card.result_json?.invoice_data,
                              );

                              const itemMatchStatus = analyzeItemMatch(
                                card.result_json?.po_data,
                                card.result_json?.grn_data,
                                card.result_json?.invoice_data,
                              );

                              const approversArray = Array.isArray(
                                card.approvers,
                              )
                                ? card.approvers
                                : card.approvers
                                  ? Object.entries(card.approvers).map(
                                      ([id, status]) => ({
                                        id,
                                        status,
                                        name: id,
                                      }),
                                    )
                                  : [];

                              const allApproved =
                                approversArray.length > 0 &&
                                approversArray.every(
                                  (a) => a.status === "APPROVED",
                                );
                              const anyRejected = approversArray.some(
                                (a) => a.status === "REJECTED",
                              );
                              const anyPending = approversArray.some(
                                (a) => a.status === "PENDING",
                              );

                              let overallStatus = "Pending";
                              let statusColor = "warning";
                              if (allApproved) {
                                overallStatus = "Approved";
                                statusColor = "success";
                              } else if (anyRejected) {
                                overallStatus = "Rejected";
                                statusColor = "error";
                              }

                              const userCanApproveReject =
                                canUserApproveReject(card);
                              const userApprovalStatus =
                                getUserApprovalStatus(card);

                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    "& td": {
                                      py: 1.8,
                                      px: 1,
                                      borderBottom: "1px solid #f1f5f9",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      fontFamily: "gilroyMedium",
                                      fontSize: "14px",
                                      color: "#475569",
                                    },
                                  }}
                                >
                                  {/* Invoice Ref ID */}
                                  <TableCell
                                    sx={{
                                      fontFamily: "gilroyBold",
                                      fontSize: "14px",
                                      width: listColumnWidths.matchCode,
                                      minWidth: listColumnWidths.matchCode,
                                      maxWidth: listColumnWidths.matchCode,
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 2,
                                      fontWeight: 600,
                                      backgroundColor: "#f8fafc",
                                      boxShadow: "2px 0 0 #e2e8f0",
                                    }}
                                  >
                                    {card.match_code || "â€”"}
                                  </TableCell>

                                  {/* Vendor */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.supplierName,
                                      minWidth: listColumnWidths.supplierName,
                                      maxWidth: listColumnWidths.supplierName,
                                    }}
                                  >
                                    <Tooltip
                                      title={
                                        po?.vendor ||
                                        grn?.vendor_name ||
                                        grn?.vendor ||
                                        "—"
                                      }
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {po?.vendor ||
                                          grn?.vendor_name ||
                                          grn?.vendor ||
                                          "â€”"}
                                      </Box>
                                    </Tooltip>
                                  </TableCell>

                                  {/* PO Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poId,
                                      minWidth: listColumnWidths.poId,
                                      maxWidth: listColumnWidths.poId,
                                    }}
                                  >
                                    {po?.purchase_order_no || "â€”"}
                                  </TableCell>

                                  {/* PO Bill Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poDate,
                                      minWidth: listColumnWidths.poDate,
                                      maxWidth: listColumnWidths.poDate,
                                    }}
                                  >
                                    {formatDate(po?.order_date)}
                                  </TableCell>

                                  {/* Invoice Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceNumber,
                                      minWidth: listColumnWidths.invoiceNumber,
                                      maxWidth: listColumnWidths.invoiceNumber,
                                    }}
                                  >
                                    {invoice?.invoice_number || "â€”"}
                                  </TableCell>

                                  {/* Invoice Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceDate,
                                      minWidth: listColumnWidths.invoiceDate,
                                      maxWidth: listColumnWidths.invoiceDate,
                                    }}
                                  >
                                    {formatDate(invoice?.invoice_date)}
                                  </TableCell>

                                  {/* GRN Number */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.grnNumber,
                                      minWidth: listColumnWidths.grnNumber,
                                      maxWidth: listColumnWidths.grnNumber,
                                    }}
                                  >
                                    {grn?.grn_number ||
                                      grn?.order_number ||
                                      "â€”"}
                                  </TableCell>

                                  {/* GRN Bill Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.grnDate,
                                      minWidth: listColumnWidths.grnDate,
                                      maxWidth: listColumnWidths.grnDate,
                                    }}
                                  >
                                    {formatDate(
                                      grn?.bill_date ||
                                        grn?.created_at ||
                                        grn?.date,
                                    )}
                                  </TableCell>

                                  {/* PO Grand Total */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.poTotal,
                                      minWidth: listColumnWidths.poTotal,
                                      maxWidth: listColumnWidths.poTotal,
                                    }}
                                  >
                                    {poTotalAmt}
                                  </TableCell>

                                  {/* Invoice Grand Total */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.invoiceTotal,
                                      minWidth: listColumnWidths.invoiceTotal,
                                      maxWidth: listColumnWidths.invoiceTotal,
                                    }}
                                  >
                                    {invoiceTotalAmt}
                                  </TableCell>

                                  {/* Quantity Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.quantityStatus,
                                      minWidth: listColumnWidths.quantityStatus,
                                      maxWidth: listColumnWidths.quantityStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Quantity matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={quantityMatchStatus.status}
                                          type={quantityMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Price Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.priceStatus,
                                      minWidth: listColumnWidths.priceStatus,
                                      maxWidth: listColumnWidths.priceStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Price matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={priceMatchStatus.status}
                                          type={priceMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Item Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.itemStatus,
                                      minWidth: listColumnWidths.itemStatus,
                                      maxWidth: listColumnWidths.itemStatus,
                                    }}
                                  >
                                    <Tooltip
                                      title="Item matching analysis"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                          },
                                        },
                                      }}
                                    >
                                      <span>
                                        <StatusChip
                                          status={itemMatchStatus.status}
                                          type={itemMatchStatus.type}
                                        />
                                      </span>
                                    </Tooltip>
                                  </TableCell>

                                  {/* Matching Date */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.matchingDate,
                                      minWidth: listColumnWidths.matchingDate,
                                      maxWidth: listColumnWidths.matchingDate,
                                    }}
                                  >
                                    {card?.created_at
                                      ? format(
                                          new Date(card.created_at),
                                          "dd MMM yyyy",
                                        )
                                      : "â€”"}
                                  </TableCell>

                                  {/* Approval Status */}
                                  <TableCell
                                    sx={{
                                      width: listColumnWidths.approvalStatus,
                                      minWidth: listColumnWidths.approvalStatus,
                                      maxWidth: listColumnWidths.approvalStatus,
                                    }}
                                  >
                                    {approversArray.length > 0 ? (
                                      <Tooltip
                                        title={
                                          <ApprovalStatusTooltip
                                            approvers={approversArray}
                                          />
                                        }
                                        arrow
                                        placement="top"
                                      >
                                        <Chip
                                          label={overallStatus}
                                          color={statusColor}
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            height: "30px",
                                            backgroundColor:
                                              overallStatus === "Approved"
                                                ? "#f0fdf4"
                                                : overallStatus === "Pending"
                                                  ? "#fffbeb"
                                                  : "#fef2f2",
                                            color:
                                              overallStatus === "Approved"
                                                ? "#166534"
                                                : overallStatus === "Pending"
                                                  ? "#d97706"
                                                  : "#dc2626",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : (
                                      <Chip
                                        label="No Status"
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          fontFamily: "gilroyMedium",
                                          fontSize: "14px",
                                          fontWeight: 600,
                                          height: "30px",
                                          backgroundColor: "#f8fafc",
                                          color: "#64748b",
                                        }}
                                      />
                                    )}
                                  </TableCell>

                                  {/* Approve/Reject Buttons */}
                                  {userIsApprover && (
                                    <TableCell
                                    sx={{
                                      position: "sticky",
                                        right: listColumnWidths.action,
                                        width: listColumnWidths.approveReject,
                                        minWidth: listColumnWidths.approveReject,
                                        maxWidth: listColumnWidths.approveReject,
                                        zIndex: 2,
                                        backgroundColor: "#f8fafc",
                                        borderLeft: "1px solid #e2e8f0",
                                        boxShadow:
                                          "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                                      }}
                                    >
                                      {userCanApproveReject ? (
                                        <Stack direction="row" spacing={0.5}>
                                          <Button
                                            variant="outlined"
                                            color="success"
                                            size="small"
                                            sx={{
                                              fontFamily: "gilroyMedium",
                                              textTransform: "none",
                                              fontWeight: 500,
                                              fontSize: "14px",
                                              px: 1,
                                              height: "34px",
                                            }}
                                            onClick={() =>
                                              approveAndRejectDoc(
                                                "accept",
                                                card.token,
                                              )
                                            }
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            sx={{
                                              fontFamily: "gilroyMedium",
                                              textTransform: "none",
                                              fontWeight: 500,
                                              fontSize: "14px",
                                              px: 1,
                                              height: "34px",
                                            }}
                                            onClick={() =>
                                              approveAndRejectDoc(
                                                "reject",
                                                card.token,
                                              )
                                            }
                                          >
                                            Reject
                                          </Button>
                                        </Stack>
                                      ) : userApprovalStatus ? (
                                        <Chip
                                          label={
                                            userApprovalStatus === "APPROVED"
                                              ? "Approved"
                                              : "Rejected"
                                          }
                                          size="small"
                                          sx={{
                                            fontFamily: "gilroyMedium",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color:
                                              userApprovalStatus === "APPROVED"
                                                ? "#fff"
                                                : "#fff",
                                            backgroundColor:
                                              userApprovalStatus === "APPROVED"
                                                ? "#47a34b"
                                                : "#dc2626",
                                            "& .MuiChip-label": {
                                              px: 1.5,
                                            },
                                          }}
                                        />
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          fontFamily="gilroyMedium"
                                          fontSize="14px"
                                        >
                                          No action required
                                        </Typography>
                                      )}
                                    </TableCell>
                                  )}

                                  {/* Actions */}
                                  <TableCell
                                    sx={{
                                      position: "sticky",
                                      right: 0,
                                      width: listColumnWidths.action,
                                      minWidth: listColumnWidths.action,
                                      maxWidth: listColumnWidths.action,
                                      zIndex: 4,
                                      backgroundColor: "#f8fafc",
                                      borderLeft: "1px solid #e2e8f0",
                                      boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 0.5,
                                        justifyContent: "space-around",
                                        alignItems: "center",
                                        "& .MuiSvgIcon-root": { fontSize: 20 },
                                      }}
                                    >
                                      <Tooltip
                                        title="View Items"
                                      
                                        componentsProps={{
                                          tooltip: {
                                            sx: {
                                              fontSize: "1.3rem",
                                              fontWeight: 500,
                                              padding: "0.8rem 1.2rem",
                                            },
                                          },
                                        }}
                                      >
                                        <IconButton
                                          size="large"
                                          onClick={() => handleViewItems(card)}
                                          color="primary"
                                          sx={{ p: 1.25 }}
                                        >
                                          <VisibilityIcon />
                                        </IconButton>
                                      </Tooltip>

                                      <DownloadMenuButton
                                        onCSV={() => downloadCSV(card)}
                                        sx={{ minWidth: 0, p: 0.5 }}
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination (backend) */}
                      <Box
                        sx={{ borderTop: "1px solid #e2e8f0", px: 2, py: 1.5 }}
                      >
                        <PaginantionComponent
                          pageNumber={pageNumber}
                          currentPage={paginationData?.current_page || 1}
                          itemsPerPage={paginationData?.total_count || 0}
                          totalItems={paginationData?.total_pages || 0}
                          localStorageKey={"three_way_matching_page_number"}
                          previouPageClick={() => {
                            previouPageClick(
                              parseInt(pageNumber, 10) || 1,
                              setPageNumber,
                            );
                            try {
                              localStorage.setItem(
                                "three_way_matching_page_number",
                                String(
                                  Math.max(
                                    1,
                                    (parseInt(String(pageNumber || 1), 10) ||
                                      1) - 1,
                                  ),
                                ),
                              );
                            } catch {}
                          }}
                          nextPageClick={() => {
                            nextPageClick(
                              parseInt(pageNumber, 10) || 1,
                              setPageNumber,
                              paginationData,
                            );
                            try {
                              localStorage.setItem(
                                "three_way_matching_page_number",
                                String(
                                  (parseInt(String(pageNumber || 1), 10) || 1) +
                                    1,
                                ),
                              );
                            } catch {}
                          }}
                          setPageNumber={(value) => {
                            setPageNumber(value);
                            try {
                              if (value)
                                localStorage.setItem(
                                  "three_way_matching_page_number",
                                  String(value),
                                );
                            } catch {}
                          }}
                          disablePrevious={
                            parseInt(pageNumber, 10) === 1 || pageNumber === ""
                          }
                          disableNext={
                            parseInt(pageNumber, 10) ===
                              paginationData?.page_count ||
                            !paginationData ||
                            pageNumber === ""
                          }
                        />
                      </Box>
                    </Paper>
                  </Box>
                </Grid>

                <TWMOverviewDashboard
                  data={matchedresponse || []}
                  summaryData={summaryResponse}
                  loading={summaryLoading}
                  onExportDashboard={downloadDashboardReportCSV}
                />
              </Grid>
            )}
          </>
        )}

        {/* UPLOAD PAGE: back button, upload panel, NO search field at all */}
        {mode === "upload" && (
          <Box
            sx={{
              minWidth: "100%",
              mx: "auto",
              p: { xs: 2, sm: 3 },
              position: "relative",
            }}
          >
            <>
              {/* Back button at top right */}
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => go("main")}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 22,
                  fontFamily: "gilroyBold",
                  fontSize: { xs: "14px", sm: "16px" },
                  minHeight: "44px",
                  px: 2.25,
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                Back
              </Button>

              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                  mt: 5,
                }}
              >
                <Typography
                  sx={{
                    flexGrow: 1,
                    fontSize: { xs: "16px", sm: "18px" },
                    fontFamily: "gilroyBold",
                    color: "#0f172a",
                  }}
                >
                  Upload PO, GRN & Invoice (PDF only)
                </Typography>
                {statusChip}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: "gilroyMedium",
                    fontSize: "14px",
                    color: "text.secondary",
                  }}
                >
                  Matching Code
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    color: "primary.main",
                    fontFamily: "gilroyBold",
                  }}
                >
                  {nextTwmCode || "â€”"}
                </Typography>
              </Box>

              {/* Drag & Drop */}
              <Box
                ref={dropRef}
                sx={{
                  border: "2px dashed #9bb8f3",
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  textAlign: "center",
                  background: isDragging ? "#eaf1ff" : "#fafcff",
                  transition: "background 160ms ease",
                  mb: 3,
                }}
              >
                <UploadFileIcon sx={{ fontSize: 28, color: "#5a6b85" }} />
                <Typography
                  sx={{
                    mt: 1,
                    mb: 2,
                    fontSize: { xs: "13px", sm: "15px" },
                    fontFamily: "gilroyMedium",
                    color: "#334155",
                  }}
                >
                  Drag & drop files here, or select from your computer
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="application/pdf,.pdf"
                  onChange={handleFileChange}
                  onClick={(e) => (e.target.value = null)}
                  style={{ display: "none" }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<CloudUploadIcon />}
                  disabled={selectedFiles.length >= TWM_REQUIRED_FILE_COUNT}
                  sx={{
                    fontFamily: "gilroyBold",
                    textTransform: "none",
                    borderRadius: 2,
                    fontSize: "14px",
                    minHeight: "42px",
                    px: 2.25,
                  }}
                >
                  Browse Files
                </Button>

                {uploadStatus === "uploading" && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <LinearProgress
                      variant="determinate"
                      value={overallProgress}
                      sx={{ borderRadius: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                      fontSize={12}
                    >
                      {overallProgress || 0}% uploaded
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#5a6b85",
                        fontSize: "14px",
                        fontFamily: "gilroyMedium",
                      }}
                    >
                      Selected files ({selectedFiles.length}/
                      {TWM_REQUIRED_FILE_COUNT})
                    </Typography>

                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      disabled={uploadStatus === "uploading"}
                      onClick={handleClearAllFiles}
                      sx={{
                        fontFamily: "gilroyBold",
                        textTransform: "none",
                        fontSize: "13px",
                        minWidth: "auto",
                        px: 0.5,
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>

                  <Stack spacing={1.2}>
                    {selectedFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${file.lastModified}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1,
                          borderRadius: 1,
                          background: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                        }}
                      >
                        <DescriptionOutlinedIcon
                          sx={{ fontSize: 20, color: "#607d8b" }}
                        />

                        {/* File name + size */}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={file.name}
                            sx={{ fontWeight: 500, fontSize: "13px" }}
                          >
                            {file.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "12px" }}
                          >
                            {formatKB(file.size)}
                          </Typography>
                        </Box>

                        {/* Remove button */}
                        <Tooltip
                          title="Remove"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: "10px", // increase tooltip text size
                              },
                            },
                          }}
                        >
                          <span>
                            <IconButton
                              aria-label="Remove file"
                              disabled={uploadStatus === "uploading"}
                              onClick={() => handleRemoveFile(index)}
                              size="small"
                            >
                              <ClearIcon color="error" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Action buttons */}
              <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                <Dbutton
                  label={
                    uploadStatus === "uploading"
                      ? "Uploading..."
                      : "Upload Documents"
                  }
                  onClick={handleUpload}
                  disabled={
                    selectedFiles.length !== TWM_REQUIRED_FILE_COUNT ||
                    uploadStatus === "uploading" ||
                    !selectedFiles.every(isPdfFile)
                  }
                />
                {uploadStatus === "uploading" && (
                  <Dbutton label="Cancel" onClick={handleCancelUpload} />
                )}
                <Dbutton
                  label={
                    saving ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} color="inherit" />
                        Saving...
                      </Stack>
                    ) : (
                      " Save Result"
                    )
                  }
                  onClick={handleSaveMatchingWithLoader}
                  disabled={
                    selectedFiles.length !== TWM_REQUIRED_FILE_COUNT ||
                    uploadStatus === "uploading" ||
                    !canSaveMatching ||
                    saving
                  }
                />
              </Stack>

              {/* Shimmer / placeholder while parsing */}
              {uploadStatus === "uploading" && !response && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress />
                  <Typography
                    variant="caption"
                    fontSize={12}
                    color="text.secondary"
                  >
                    Parsing documentsâ€¦
                  </Typography>
                </Box>
              )}
            </>

            {/* Results */}
            {response && (
              <Box sx={{ mt: 3 }}>
                <ThreeWayMatchTable
                  poData={response.po_data}
                  invoiceData={response.invoice_data}
                  grnData={response.grn_data}
                  showSummary={true}
                />
              </Box>
            )}
          </Box>
        )}

        {mode === "view" && (
          <Box sx={{ p: { xs: 2, sm: 3 }, position: "relative" }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => go("main")}
              sx={{
                position: "absolute",
                top: 0,
                right: 22,
                fontFamily: "gilroyBold",
                fontSize: { xs: "14px", sm: "16px" },
                minHeight: "44px",
                px: 2.25,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Back
            </Button>

            <Typography
              variant="subtitle2"
              sx={{ fontSize: "13px", color: "text.secondary", mb: 2 }}
            >
              Code:{" "}
              <strong style={{ color: "#1976d2" }}>
                {savedTwmCode || "â€”"}
              </strong>
            </Typography>

            {!viewData ? (
              <Alert severity="info">No saved matching found.</Alert>
            ) : (
              <ThreeWayMatchTable
                poData={viewData.poData}
                invoiceData={viewData.invoiceData}
                grnData={viewData.grnData}
              />
            )}
          </Box>
        )}

        {selectedCard && (
          <ThreeWayMatchTable
            poData={selectedCard.po_data}
            grnData={selectedCard.grn_data}
            invoiceData={selectedCard.invoice_data}
            open={viewItemsDialog}
            onClose={() => setViewItemsDialog(false)}
            showSummary={false} // <-- ADD THIS LINE
          />
        )}

        <Dialog
          open={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #e7eefc",
              boxShadow: "0 24px 70px rgba(15, 23, 42, 0.18)",
              minWidth: { xs: 320, sm: 480 },
              background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
            },
          }}
        >
          <Box sx={{ p: 3.5 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background:
                    "linear-gradient(135deg, rgba(34,197,94,0.14) 0%, rgba(16,185,129,0.10) 100%)",
                  border: "1px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ color: "#10b981", fontSize: 26 }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "GilroyBold",
                    fontSize: { xs: 20, sm: 22 },
                    lineHeight: 1.2,
                    color: "#0f172a",
                  }}
                >
                  Matching Saved!
                </Typography>
                <Typography
                  sx={{
                    mt: 0.4,
                    fontFamily: "GilroyMedium",
                    fontSize: { xs: 13.5, sm: 14.5 },
                    color: "#475569",
                  }}
                >
                  Your Three-Way Matching was successfully saved.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                mb: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "GilroySemiBold",
                  fontSize: 13,
                  color: "#64748b",
                }}
              >
                Code
              </Typography>
              <Box
                sx={{
                  mt: 0.9,
                  display: "inline-flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  background: "rgba(25, 118, 210, 0.10)",
                  border: "1px solid rgba(25, 118, 210, 0.22)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "GilroyExtraBold",
                    fontSize: { xs: 22, sm: 26 },
                    letterSpacing: "0.6px",
                    color: "#1976d2",
                  }}
                >
                  {savedTwmCode || "â€”"}
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: "primary.main", mb: 2, display: "none" }}
            >
              {savedTwmCode || "â€”"}
            </Typography>
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                go("main");
              }}
              variant="contained"
              fullWidth
              sx={{
                height: 46,
                borderRadius: 3,
                textTransform: "none",
                fontFamily: "GilroyBold",
                fontSize: 16,
                boxShadow: "0 10px 24px rgba(25, 118, 210, 0.25)",
              }}
            >
              OK
            </Button>
          </Box>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.type}
            variant="filled"
            sx={{
              width: "100%",
              "& .MuiAlert-message": {
                fontSize: LARGE_SNACK_MESSAGES.has(snack.msg) ? "16px" : "14px",
                fontFamily: "gilroyMedium",
                fontWeight: LARGE_SNACK_MESSAGES.has(snack.msg) ? 600 : 500,
              },
            }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>

      <Modal open={openOfflinePopup}>
        <Fade in={openOfflinePopup}>
          <Box>
            <SuccessPopupDashboard
              popupImage={deletePopupImg}
              popupHeroText="Offline Mode"
              popupDescription="Data will sync once internet is available."
              popBtn={true}
              btnText={"Okay"}
              vendorBtn={true}
              btnClick={() => setOpenOfflinePopup(false)}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default ThreeWayMatching;
